import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useMagneticEffect } from "@/hooks/use3DTilt";

const Hero = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const magneticBtn1 = useMagneticEffect(0.4);
  const magneticBtn2 = useMagneticEffect(0.4);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center px-6 py-20 overflow-hidden">
      <div className="container max-w-6xl mx-auto relative z-10">
        <div className={`flex flex-col items-center text-center space-y-8 transition-all duration-1000 ${
          isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        }`}>
          {/* Main Content */}
          <div className="space-y-6 max-w-5xl">
            <div className={`space-y-6 transition-all duration-700 delay-200 ${
              isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
            }`}>
              <h1 className="text-xl md:text-2xl lg:text-3xl font-bold leading-snug text-violet-400 text-balance mx-auto max-w-5xl [text-shadow:0_0_40px_rgba(167,139,250,0.8),0_0_80px_rgba(139,92,246,0.5)]">
                Integrating cutting-edge AI into production-grade systems that deliver measurable business impact and lasting, real-world growth
              </h1>
              <div className="flex items-center justify-center gap-3 pt-2">
                <div className="h-[2px] w-24 bg-gradient-to-r from-transparent via-primary/50 to-transparent"></div>
                <div className="h-2 w-2 bg-primary rounded-full animate-pulse shadow-lg shadow-primary/50"></div>
                <div className="h-[2px] w-24 bg-gradient-to-r from-transparent via-primary/50 to-transparent"></div>
              </div>
            </div>

            <div className={`flex flex-col items-center gap-1 pt-2 transition-all duration-700 delay-300 ${
              isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
            }`}>
              <p className="font-display italic text-2xl md:text-3xl text-violet-400/90 leading-none">
                — Spilios Spiliopoulos
              </p>
              <p className="text-xs md:text-sm font-semibold tracking-[0.3em] uppercase text-muted-foreground">
                CTO &amp; AI Architect
              </p>
            </div>

            {/* Stats Row */}
            <div className={`flex flex-wrap justify-center gap-8 md:gap-12 pt-6 transition-all duration-700 delay-400 ${
              isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
            }`}>
              {[
                { value: "8+", label: "Months as CTO" }
              ].map((stat, index) => (
                <div
                  key={index}
                  className="space-y-2 group cursor-default relative"
                  style={{ transitionDelay: `${400 + index * 100}ms` }}
                >
                  <div className="relative">
                    <div className="text-4xl md:text-5xl font-bold text-primary group-hover:scale-110 transition-transform duration-300">
                      {stat.value}
                    </div>
                    <div className="text-sm text-muted-foreground uppercase tracking-wider group-hover:text-foreground transition-colors duration-300">
                      {stat.label}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className={`flex flex-col sm:flex-row gap-6 pt-8 justify-center transition-all duration-700 delay-500 ${
              isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
            }`}>
              <Button
                ref={magneticBtn1.ref}
                onMouseMove={magneticBtn1.onMouseMove}
                onMouseLeave={magneticBtn1.onMouseLeave}
                size="lg"
                className="magnetic-btn shadow-2xl shadow-primary/20 hover:shadow-primary/40 transition-all text-lg px-8 py-6 hover:scale-105 group relative overflow-hidden"
                onClick={() => document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' })}
              >
                <span className="relative z-10 flex items-center gap-2">
                  View Projects
                  <span className="group-hover:translate-x-1 transition-transform duration-300">→</span>
                </span>
              </Button>
              <Button
                ref={magneticBtn2.ref}
                onMouseMove={magneticBtn2.onMouseMove}
                onMouseLeave={magneticBtn2.onMouseLeave}
                size="lg"
                variant="outline"
                className="magnetic-btn border-2 border-primary/30 hover:border-primary/60 text-lg px-8 py-6 hover:scale-105 transition-all group backdrop-blur-sm"
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              >
                <span className="group-hover:text-primary transition-colors duration-300">Get in Touch</span>
              </Button>
            </div>

            {/* Scroll Indicator */}
            <div className={`flex justify-center pt-12 transition-all duration-700 delay-700 ${
              isLoaded ? "opacity-100" : "opacity-0"
            }`}>
              <div
                className="flex flex-col items-center gap-2 cursor-pointer group"
                onClick={() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' })}
              >
                <span className="text-xs uppercase tracking-widest text-muted-foreground group-hover:text-foreground transition-colors duration-300">
                  Scroll to explore
                </span>
                <div className="w-6 h-10 border-2 border-muted-foreground/30 rounded-full flex justify-center group-hover:border-primary/50 transition-colors duration-300">
                  <div className="w-1.5 h-3 bg-primary rounded-full mt-2 animate-bounce" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
